<template>
  <div
    v-if="length > 1"
    class="base-pagination"
    :style="styles"
    role="region"
    aria-label="Pagination"
  >
    <button
      v-if="!hideGotoFirst"
      class="base-pagination--page-item accessibility-outline outline-inset"
      :disabled="currentPage <= 1"
      type="button"
      aria-label="Zur ersten Seite"
      @click="changePage(1)"
    >
      <span>«</span>
    </button>
    <button
      class="base-pagination--page-item accessibility-outline outline-inset"
      :disabled="currentPage <= 1"
      type="button"
      aria-label="Zur vorherigen Seite"
      @click="changePage(--activePage)"
    >
      <span>‹</span>
    </button>
    <div class="base-pagination-page-selection">
      <button
        v-for="page in pageRange"
        :key="page"
        class="base-pagination--page-item accessibility-outline outline-inset"
        :class="{
          active: page === activePage,
        }"
        type="button"
        :aria-label="`Zur Seite ${page}`"
        @click="changePage(page)"
      >
        <span>{{ page }}</span>
      </button>
    </div>
    <button
      class="base-pagination--page-item accessibility-outline outline-inset"
      :disabled="currentPage >= length"
      type="button"
      aria-label="Zur nächsten Seite"
      @click="changePage(++activePage)"
    >
      <span>›</span>
    </button>
    <button
      v-if="!hideGotoLast"
      class="base-pagination--page-item accessibility-outline outline-inset"
      :disabled="currentPage >= length"
      type="button"
      aria-label="Zur letzten Seite"
      @click="changePage(length)"
    >
      <span>»</span>
    </button>
  </div>
</template>

<script lang="ts" setup>
import {
  ref, watch, onMounted,
} from 'vue';

const props = withDefaults(defineProps<{
  currentPage: number;
  length: number;
  maxPages?: number;
  hideGotoFirst?: boolean;
  hideGotoLast?: boolean;
}>(), {
  currentPage: 1,
  length: 1,
  maxPages: 5,
  hideGotoFirst: false,
  hideGotoLast: false,
});

const emit = defineEmits(['change']);

const activePage = ref(props.currentPage);
const pages = ref(props.maxPages);
const pageRange = ref<number[]>([]);

const range = (size: number, startAt = 0) => {
  function* rangeGen() {
    for (let i = startAt; i < size + startAt; i += 1) {
      yield i;
    }
  }

  return Array.from(rangeGen());
};
const getPageRange = (pageLength: number = 0) => {
  if (props.length < props.maxPages) {
    pages.value = props.length;
  } else {
    pages.value = pageLength || props.maxPages;
  }

  const middleRangeMod = Math.floor((pageLength || props.maxPages) / 2);

  // ensure activePage isn't out of range
  if (activePage.value < 1) {
    activePage.value = 1;
  } else if (activePage.value > props.length) {
    activePage.value = props.length;
  }

  let rangeStart = 0;
  if (activePage.value <= middleRangeMod || props.length < (pageLength || props.maxPages)) {
    rangeStart = 1;
  } else if (activePage.value > props.length - middleRangeMod) {
    rangeStart = props.length - middleRangeMod * 2;
  } else {
    rangeStart = activePage.value - middleRangeMod;
  }

  const size = pages.value > props.length ? props.length : pages.value;

  return range(size, rangeStart);
};

watch(() => props.currentPage, () => {
  activePage.value = props.currentPage;

  if (props.currentPage >= 100) {
    pageRange.value = getPageRange(3);
  } else {
    pageRange.value = getPageRange();
  }
});

watch(() => JSON.stringify(`${props.length}${props.maxPages}`), () => {
  pageRange.value = getPageRange();
});

onMounted(() => {
  pageRange.value = getPageRange();
});

const changePage = (payload: number) => {
  activePage.value = payload;

  if (payload > 100) {
    pageRange.value = getPageRange(3);
  }
  emit('change', payload);
};

const itemWidth = 'min-content';
const stylePaginationColumns = () => {
  if (props.hideGotoFirst && props.hideGotoLast) {
    return `repeat(3, ${itemWidth})`;
  }
  if (props.hideGotoFirst || props.hideGotoLast) {
    return `repeat(4, ${itemWidth})`;
  }

  return `repeat(5, ${itemWidth})`;
};

const styles = ref({
  '--style-pagination-columns': stylePaginationColumns(),
  '--style-pagination-page-columns': `repeat(${pages.value}, minmax(${itemWidth}, 10rem))`,
});

defineExpose({
  activePage,
  pages,
  pageRange,
  getPageRange,
  changePage,
  styles,
});
</script>

<style lang="scss" scoped>
.base-pagination {
  display: grid;
  justify-content: flex-end;
  grid-template-columns: var(--style-pagination-columns);

  &--page-item {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color .15s;

    min-width: 2rem;
    height: 2.4rem;
    padding: 0 0.5rem;

    border: none;
    background-color: transparent;
    color: #212529;

    &:hover {
      background-color: #e9ecef;
    }

    &.active {
      font-weight: 700;
      color: #000;
    }

    &:disabled {
      color: #ababab;
    }
  }
}

.base-pagination-page-selection {
    display: grid;
    grid-template-columns: var(--style-pagination-page-columns);
}
</style>
